import { X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * BulkActionBar — sticky bottom bar shown when a DataTable has selection.
 * Centered, floating, dismissible. Slides up when `count > 0`.
 */
export interface BulkActionBarProps extends React.HTMLAttributes<HTMLDivElement> {
  count: number
  onClear: () => void
  actions?: React.ReactNode
}

export const BulkActionBar = React.forwardRef<HTMLDivElement, BulkActionBarProps>(
  ({ className, count, onClear, actions, ...props }, ref) => {
    if (count === 0) return null
    return (
      <div
        ref={ref}
        role="region"
        aria-label="Bulk actions"
        className={cn(
          'fixed bottom-6 left-1/2 -translate-x-1/2 z-40',
          'flex items-center gap-3 px-4 py-3 rounded-pill',
          'bg-card border border-ia-border shadow-lg',
          'animate-in slide-in-from-bottom-2 fade-in duration-200',
          className,
        )}
        {...props}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear selection"
            className="rounded-pill h-7 w-7 flex items-center justify-center text-ia-text-tertiary transition-all hover:bg-[var(--color-ia-sidebar-active-glow)] hover:text-[var(--color-ia-sidebar-active-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X size={14} strokeWidth={1.75} />
          </button>
          <span className="text-body-sm font-medium text-ia-text tabular-nums">
            {count} selected
          </span>
        </div>
        <div className="h-5 w-px bg-ia-border" />
        <div className="flex items-center gap-1">{actions}</div>
      </div>
    )
  },
)
BulkActionBar.displayName = 'BulkActionBar'
